import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminTitle, Spinner } from "@/admin/AdminLayout";
import { renderField, TextF, AreaF } from "@/admin/fields";

const GROUPS = {
  firma: [
    { key: "company_name", label: "Marka adı" }, { key: "legal_name", label: "Ticari unvan" }, { key: "founded", label: "Kuruluş tarihi" },
    { key: "address", label: "Adres", type: "area", rows: 2 }, { key: "phone", label: "Telefon (görünen)" },
    { key: "whatsapp", label: "WhatsApp numarası", hint: "Ülke koduyla, yalnızca rakam: 905052230009" },
    { key: "whatsapp_message", label: "WhatsApp hazır mesajı", type: "area", rows: 2 },
    { key: "logo_main", label: "Ana logo (kalkan)", type: "image" }, { key: "logo_alt", label: "Yardımcı logo (alt bilgi)", type: "image" },
    { key: "service_regions", label: "Hizmet bölgesi kapsamı", type: "lines" }, { key: "service_regions_note", label: "Hizmet bölgesi notu", type: "area", rows: 2 },
  ],
  kurumsal: [
    { key: "email", label: "Kurumsal e-posta" }, { key: "show_email", label: "E-posta adresini sitede göster", type: "check" },
    { key: "maps_url", label: "Google Haritalar bağlantısı (işletme profili)", hint: "\"Google Haritalar'da Görüntüle\" butonu bu adresi açar" },
    { key: "maps_directions_url", label: "Yol tarifi bağlantısı" },
    { key: "maps_embed_query", label: "Gömülü harita konumu", hint: "Koordinat (enlem,boylam) veya işletme adı" },
    { key: "working_hours", label: "Çalışma günleri ve saatleri", type: "pairs", keys: ["day", "hours"], labels: ["Gün(ler) – örn. Pazartesi – Cuma", "Saat – örn. 09:00 – 18:00 veya 7/24 Açık"] },
    { key: "hours_note", label: "Çalışma saatleri notu (isteğe bağlı)" },
  ],
  anasayfa: [
    { key: "hero_title", label: "Karşılama başlığı" }, { key: "hero_subtitle", label: "Karşılama alt başlığı", type: "area", rows: 2 },
    { key: "hero_image", label: "Karşılama görseli", type: "image" }, { key: "intro_title", label: "Tanıtım başlığı" },
    { key: "intro_text", label: "Tanıtım metni", type: "area", rows: 5 }, { key: "video_url", label: "Tanıtım videosu (MP4 adresi)", type: "image" },
    { key: "video_poster", label: "Video kapak görseli", type: "image" }, { key: "values_image", label: "Değerler görseli (Hakkımızda)", type: "image" },
    { key: "why_items", label: "Neden Rüzgar Özel Güvenlik? maddeleri", type: "pairs", keys: ["title", "text"], labels: ["Başlık", "Açıklama"] },
    { key: "cta_title", label: "Teklif çağrısı başlığı" }, { key: "cta_text", label: "Teklif çağrısı metni", type: "area", rows: 2 },
  ],
  formlar: [
    { key: "notification_emails", label: "Bildirim e-posta adresleri", type: "lines", hint: "Teklif, başvuru ve mesaj bildirimlerinin gideceği adresler (en fazla 5)" },
    { key: "reply_to", label: "Yanıt adresi (Reply-To)", hint: "Bildirim e-postalarına yanıt verildiğinde gidecek adres (isteğe bağlı)" },
    { key: "facility_types", label: "Teklif formu: tesis türleri", type: "lines" }, { key: "shift_options", label: "Teklif formu: çalışma düzeni seçenekleri", type: "lines" },
    { key: "career_positions", label: "Başvuru formu: pozisyonlar", type: "lines" },
  ],
};
const SOCIALS = [["instagram", "Instagram"], ["facebook", "Facebook"], ["linkedin", "LinkedIn"], ["youtube", "YouTube"], ["x", "X (Twitter)"]];
const SEO_PAGES = [["home", "Ana Sayfa"], ["services", "Hizmetlerimiz"], ["sectors", "Sektörler"], ["blog", "Blog"], ["faq", "SSS"], ["contact", "İletişim"], ["quote", "Teklif Al"]];

const LIST_KEYS = ["notification_emails", "facility_types", "shift_options", "career_positions", "service_regions"];

function Group({ g, v, setV }) {
  return <div className="space-y-5 rounded-lg border border-zinc-200 bg-white p-5">{GROUPS[g].map((f) => renderField(f, v[f.key], (val) => setV({ ...v, [f.key]: val })))}</div>;
}

function KurumsalTab({ v, setV }) {
  return (
    <>
      <Group g="kurumsal" v={v} setV={setV} />
      <div className="mt-5 grid gap-4 rounded-lg border border-zinc-200 bg-white p-5 sm:grid-cols-2">
        <p className="text-sm font-bold sm:col-span-2">Sosyal medya adresleri (boş bırakılanlar sitede gösterilmez)</p>
        {SOCIALS.map(([k, l]) => <TextF key={k} label={l} value={v.socials?.[k]} onChange={(val) => setV({ ...v, socials: { ...v.socials, [k]: val } })} testid={`social-${k}`} />)}
      </div>
      <div className="mt-5 rounded-lg border border-amber-300 bg-amber-50 p-5 text-sm text-amber-900" data-testid="google-integration-status">
        <p className="font-bold">Google entegrasyon durumu: Bağlı değil (manuel)</p>
        <p className="mt-1">Çalışma saatleri Google İşletme Profili'nden otomatik alınmıyor. Google profilinizdeki saatler değiştiğinde yukarıdaki alanı da güncelleyin. Otomatik senkronizasyon için Google Business Profile API erişim onayı ve işletme sahibi hesabıyla yetkilendirme gerekir.</p>
      </div>
    </>
  );
}

function SeoTab({ v, setV }) {
  const setSeo = (k, part) => (val) => setV({ ...v, seo: { ...v.seo, [k]: { ...v.seo?.[k], [part]: val } } });
  return (
    <div className="space-y-5 rounded-lg border border-zinc-200 bg-white p-5">
      <TextF label="Google Search Console doğrulama kodu" value={v.gsc_code} onChange={(val) => setV({ ...v, gsc_code: val })} testid="field-gsc_code" hint='Yalnızca meta etiketindeki content="..." değerini girin. Boşsa kod eklenmez.' />
      <TextF label="Google Analytics ölçüm kimliği" value={v.ga_id} onChange={(val) => setV({ ...v, ga_id: val })} testid="field-ga_id" hint="G-XXXXXXX biçiminde. Yalnızca ziyaretçi çerezleri kabul ederse çalışır." />
      {SEO_PAGES.map(([k, l]) => (
        <div key={k} className="grid gap-3 border-t border-zinc-100 pt-4 sm:grid-cols-2">
          <TextF label={`${l} – SEO başlığı`} value={v.seo?.[k]?.title} onChange={setSeo(k, "title")} testid={`seo-${k}-title`} />
          <AreaF label={`${l} – SEO açıklaması`} rows={2} value={v.seo?.[k]?.description} onChange={setSeo(k, "description")} testid={`seo-${k}-desc`} />
        </div>
      ))}
    </div>
  );
}

async function runWith(setFlag, fn) {
  setFlag(true);
  try {
    await fn();
  } catch (e) {
    toast.error(errMsg(e));
  } finally {
    setFlag(false);
  }
}

export default function SettingsPage() {
  const [v, setV] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setV(r.data));
  }, []);
  if (!v) return <Spinner />;
  const save = () => runWith(setSaving, async () => {
    const body = { ...v };
    LIST_KEYS.forEach((k) => { body[k] = (body[k] || []).filter((s) => s.trim()); });
    const r = await api.put("/admin/settings", body);
    setV(r.data);
    toast.success("Ayarlar kaydedildi");
  });
  const testMail = () => runWith(setTesting, async () => {
    const r = await api.post("/admin/settings/test-email");
    toast.success(`Test e-postası gönderildi: ${r.data.sent_to.join(", ")}`);
  });

  return (
    <div>
      <AdminTitle title="Site Ayarları">
        <button onClick={save} disabled={saving} className="btn-red !py-2" data-testid="settings-save-btn">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet</button>
      </AdminTitle>
      <Tabs defaultValue="firma">
        <TabsList className="mb-4 flex h-auto flex-wrap justify-start">
          <TabsTrigger value="firma" data-testid="settings-tab-firma">Firma ve İletişim</TabsTrigger>
          <TabsTrigger value="kurumsal" data-testid="settings-tab-kurumsal">Kurumsal Bilgiler ve Sosyal Medya</TabsTrigger>
          <TabsTrigger value="anasayfa" data-testid="settings-tab-anasayfa">Ana Sayfa</TabsTrigger>
          <TabsTrigger value="formlar" data-testid="settings-tab-formlar">Formlar ve Bildirim</TabsTrigger>
          <TabsTrigger value="seo" data-testid="settings-tab-seo">SEO ve Analitik</TabsTrigger>
        </TabsList>
        <TabsContent value="firma"><Group g="firma" v={v} setV={setV} /></TabsContent>
        <TabsContent value="kurumsal"><KurumsalTab v={v} setV={setV} /></TabsContent>
        <TabsContent value="anasayfa"><Group g="anasayfa" v={v} setV={setV} /></TabsContent>
        <TabsContent value="formlar">
          <Group g="formlar" v={v} setV={setV} />
          <button onClick={testMail} disabled={testing} className="btn-outline mt-4" data-testid="test-email-btn">{testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Test e-postası gönder (önce kaydedin)</button>
        </TabsContent>
        <TabsContent value="seo"><SeoTab v={v} setV={setV} /></TabsContent>
      </Tabs>
    </div>
  );
}
